import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  RadioGroup,
  Radio,
  Text,
  Progress,
  Stack,
  Card,
  CardBody,
  SimpleGrid,
  HStack,
} from '@chakra-ui/react';
import axios from '../../axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

const SurveyQuestions = () => {
  const { endpoint } = useParams();
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [pageInfo, setPageInfo] = useState({ position: 1, title: '' });
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Helper: get current page (global counter) from session (localStorage)
  const getCurrentPage = () => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    return surveySession.page ? Number(surveySession.page) : 1;
  };

  // Helper to update current page in session (localStorage)
  const updateLocalPage = (newPage) => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    surveySession.page = newPage;
    localStorage.setItem('survey_session', JSON.stringify(surveySession));
  };

  // Redirect to acknowledgment if respondent details are missing.
  useEffect(() => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session'));
    if (!surveySession?.group || !surveySession?.role) {
      navigate('../acknowledgment');
    }
  }, [navigate]);

  // Helper to load persisted answers for the questions on the current page.
  const loadPersistedAnswers = (questions) => {
    const storedAnswers = JSON.parse(localStorage.getItem('survey_answers')) || {};
    const initialAnswers = {};
    questions.forEach((q) => {
      if (storedAnswers[q.id] !== undefined) {
        initialAnswers[q.id] = storedAnswers[q.id];
      } else {
        initialAnswers[q.id] = q.question_type === 'slider' ? 4 : '';
      }
    });
    return initialAnswers;
  };

  // Debounced function to fetch questions for a given page.
  const fetchQuestions = debounce(async (page = null, from_complete = null) => {
    try {
      const surveySession = JSON.parse(localStorage.getItem('survey_session'));
      let pageToLoad = page !== null ? page : getCurrentPage();
      const params = {
        uuid: surveySession.uuid,
        group_id: surveySession.group,
        page: pageToLoad,
        from_complete: from_complete,
      };

      const response = await axios.get(`/public_surveys/${endpoint}/questions`, { params });
      if (response.data.final) {
        navigate('../complete');
        return;
      }
      if (response.data.completed) {
        navigate('../complete');
        return;
      }
      const { page: newPageInfo, questions: newQuestions, total_pages } = response.data;
      // Save total_pages in session for later use.
      surveySession.total_pages = total_pages;
      localStorage.setItem('survey_session', JSON.stringify(surveySession));

      setPageInfo(newPageInfo);
      setQuestions(newQuestions);
      const initialAnswers = loadPersistedAnswers(newQuestions);
      setAnswers(initialAnswers);
      setProgress(((newPageInfo.position - 1) / total_pages) * 100);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  }, 300);

  useEffect(() => {
    fetchQuestions();
  }, [endpoint]);

  // Persist answer changes.
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev, [questionId]: value };
      const storedAnswers = JSON.parse(localStorage.getItem('survey_answers')) || {};
      storedAnswers[questionId] = value;
      localStorage.setItem('survey_answers', JSON.stringify(storedAnswers));
      return newAnswers;
    });
  };

  // Next button: submit answers and then load the next page.
  const handleSubmit = async () => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session'));
    const unanswered = Object.values(answers).some((value) => value === null || value === '');
    if (unanswered) {
      alert(t('public_survey.questions.unansweredAlert'));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/public_surveys/${endpoint}/answers`, {
        uuid: surveySession.uuid,
        group_id: surveySession.group,
        responses: answers,
        page: pageInfo.position,
      });
      if (response.data.completed) {
        navigate('../complete');
      } else if (response.data.final) {
        updateLocalPage(response.data.next_page || (getCurrentPage() + 1));
        navigate('../complete');
      } else {
        const nextPage = response.data.next_page;
        updateLocalPage(nextPage);
        fetchQuestions(nextPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
    } finally {
      setLoading(false);
    }
  };

  // New: update respondent page in DB by calling update_page endpoint.
  const updatePageInDB = async (newPage) => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    try {
      await axios.post(`/public_surveys/${endpoint}/update_page`, {
        uuid: surveySession.uuid,
        page: newPage,
      });
    } catch (error) {
      console.error('Error updating page in DB:', error);
    }
  };

  // Back button: if on page 1, navigate directly to Acknowledgment; otherwise, load previous page.
  const handleBack = async () => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    const currentPage = getCurrentPage();
    if (currentPage === 1) {
      navigate('../acknowledgment');
      return;
    }
    const targetPage = currentPage - 1;
    // Update DB to set the new page value.
    await updatePageInDB(targetPage);
    try {
      const response = await axios.get(`/public_surveys/${endpoint}/questions`, {
        params: {
          uuid: surveySession.uuid,
          group_id: surveySession.group,
          page: targetPage,
        },
      });
      updateLocalPage(targetPage);
      if (response.data.completed) {
        navigate('../complete');
        return;
      }
      const { page: newPageInfo, questions: newQuestions, total_pages } = response.data;
      setPageInfo(newPageInfo);
      setQuestions(newQuestions);
      const initialAnswers = loadPersistedAnswers(newQuestions);
      setAnswers(initialAnswers);
      setProgress(((newPageInfo.position - 1) / total_pages) * 100);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error fetching previous questions:', error);
    }
  };

  return (
    <Box p={{ base: 4, md: 6 }} mx={{base: 0, md: "auto"}} maxW={{ base: '95%', md: '800px' }} w="100%">
      <Box mb={{ base: 4, md: 6 }} textAlign="center" w="100%">
        <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="bold">
          {pageInfo.title}
        </Text>
        <Progress value={progress} size={{ base: 'sm', md: 'md' }} colorScheme="blue" bgColor="#4EB9C5" variant="outline" mt={2} />
      </Box>

      <VStack spacing={{ base: 4, md: 6 }} w="100%">
        {questions.map((q) => (
          <Card
            key={q.id}
            shadow="md"
            p={{ base: 3, md: 4 }}
            maxW={{ base: '100%', md: '600px' }}
            w="100%"
            borderWidth="1px"
            borderRadius="md"
            mx="auto"
          >
            <CardBody>
              <FormControl>
                <FormLabel fontSize={{ base: 'md', md: 'lg' }}>{q.question_text}</FormLabel>
                {q.question_type === 'slider' ? (
                  <Box position="relative" pt={{ base: 3, md: 4 }}>
                    <Slider
                      min={1}
                      max={7}
                      step={1}
                      colorScheme="blue"
                      value={answers[q.id] || 4}
                      onChange={(val) => handleAnswerChange(q.id, val)}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxShadow="md" bgColor="#e70" />
                    </Slider>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      mt={2}
                      fontSize={{ base: 'xs', md: 'sm' }}
                      color="gray.600"
                    >
                      <Text>{t('public_survey.questions.stronglyDisagree')}</Text>
                      <Text>{t('public_survey.questions.stronglyAgree')}</Text>
                    </Box>
                  </Box>
                ) : q.question_type === 'bullets' ? (
                  <>
                    <RadioGroup value={answers[q.id]?.toString() || ''} onChange={(val) => handleAnswerChange(q.id, parseInt(val, 10))}>
                      <Stack direction="row" spacing={{ base: 1, md: 3 }} justify="center">
                        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                          <Radio
                            key={num}
                            value={num.toString()}
                            mx={{ base: 0.5, md: 1.5 }}
                            fontSize={{ base: 'xs', md: 'sm' }}
                          >
                            {num}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      mt={1}
                      fontSize={{ base: 'xs', md: 'sm' }}
                      color="gray.600"
                    >
                      <Text>{t('public_survey.questions.stronglyDisagree')}</Text>
                      <Text>{t('public_survey.questions.stronglyAgree')}</Text>
                    </Box>
                  </>
                ) : q.question_type === 'buttons' ? (
                  <SimpleGrid columns={7} spacing={1}>
                    {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                      <Button
                        key={num}
                        colorScheme={answers[q.id] === num ? 'blue' : 'gray'}
                        onClick={() => handleAnswerChange(q.id, num)}
                        size={{ base: 'xs', md: 'sm' }}
                      >
                        {num}
                      </Button>
                    ))}
                  </SimpleGrid>
                ) : null}
              </FormControl>
            </CardBody>
          </Card>
        ))}
      </VStack>

      <HStack mt={{ base: 6, md: 10 }} justifyContent="space-between" w="100%">
        <Button
          colorScheme="teal"
          variant="outline"
          onClick={handleBack}
          _hover={{ bg: 'white', color: 'teal.600' }}
          flex="1"
          mr={{ base: 0, md: 2 }}
          size={{ base: 'sm', md: 'md' }}
        >
          {t('public_survey.questions.back')}
        </Button>
        <Button
          colorScheme="teal"
          variant="outline"
          onClick={handleSubmit}
          isLoading={loading}
          loadingText={t('public_survey.questions.loading')}
          isDisabled={loading}
          _hover={{ bg: 'teal.600', color: 'white' }}
          flex="1"
          ml={{ base: 0, md: 2 }}
          size={{ base: 'sm', md: 'md' }}
        >
          {t('public_survey.questions.next')}
        </Button>
      </HStack>
    </Box>
  );
};

export default SurveyQuestions;